import styled from 'styled-components';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useContext } from 'react';
import P1 from '@base/P1/P1';
import dateHelper from '@utils/dates';
// import tagHelper from '@utils/tagHelper';
import { toggleLoginModal } from '@actions/auth';
import { useDispatchToStore } from '@hooks/reduxHooks';
import {
  Spacings,
  Fonts,
  mobile,
  BgColors
} from '../../../../../styleConstants';
import { Context as IsMobileContext } from '../../../../Context/IsMobileContext';
import DotsBg from '../../DotsBg';
import ShareContainer from '../../ShareContainer/ShareContainer';
// import { Live, Play } from '../../SummitSchedule/CardIcons';

const Wrapper = styled.div``;

const SpeakerContainer = styled.div`
  border-radius: ${Spacings.SPACING_5B} ${Spacings.SPACING_5B}
    ${Spacings.SPACING_0B} ${Spacings.SPACING_0B};
  background-color: ${({ $bgColor }) => $bgColor};
  position: relative;
  padding: ${Spacings.SPACING_0B} ${Spacings.SPACING_6B};
  display: grid;
  align-items: center;
  grid-template: 1fr / max-content 1fr;
  grid-column-gap: ${Spacings.SPACING_6B};
  overflow: hidden;
  @media only screen and (max-width: ${mobile.maxWidth}) {
    grid-column-gap: ${Spacings.SPACING_0B};
    padding: ${Spacings.SPACING_0B} ${Spacings.SPACING_2B}
      ${Spacings.SPACING_0B} ${Spacings.SPACING_6B};
  }
`;

const CardBackground = styled(DotsBg)`
  position: absolute;
  top: calc(100% - ${Spacings.SPACING_16B});
  right: -${Spacings.SPACING_9B};
  @media only screen and (max-width: ${mobile.maxWidth}) {
    top: calc(100% - ${Spacings.SPACING_10B});
    right: -${Spacings.SPACING_15B};
  }
`;

const Image = styled.img`
  user-drag: none;
  width: 200px;
  @media only screen and (max-width: ${mobile.maxWidth}) {
    width: 148px;
  }
`;

const ContentWrapper = styled.div`
  position: relative;
  z-index: 1;
`;

const SpeakerName = styled.div`
  font-weight: ${Fonts.SEMIBOLD};
  font-size: 40px;
  line-height: 49px;
  color: ${({ $color }) => $color};
  @media only screen and (max-width: ${mobile.maxWidth}) {
    font-size: ${Fonts.H3};
    line-height: 30px;
  }
`;

const SubText = styled.div`
  font-weight: ${Fonts.REGULAR};
  font-size: ${Fonts.H3};
  line-height: 30px;
  padding-top: ${Spacings.SPACING_1B};
  color: ${({ $color }) => $color};
  @media only screen and (max-width: ${mobile.maxWidth}) {
    font-size: ${Fonts.H5};
    line-height: ${Fonts.HEADER_LINE_HEIGHT};
  }
`;

const InfoContainer = styled.div`
  padding: ${Spacings.SPACING_10B} ${Spacings.SPACING_10B}
    ${Spacings.SPACING_8B} ${Spacings.SPACING_10B};
  display: grid;
  grid-template: 1fr ${Spacings.SPACING_12B} / 2.4fr 3fr;
  grid-column-gap: ${Spacings.SPACING_10B};
  grid-row-gap: ${Spacings.SPACING_4B};
  /* height: 280px; */
  background-color: ${BgColors.WHITE};
  border-radius: ${Spacings.SPACING_0B} ${Spacings.SPACING_0B}
    ${Spacings.SPACING_5B} ${Spacings.SPACING_5B};
  @media only screen and (max-width: ${mobile.maxWidth}) {
    padding: ${Spacings.SPACING_4B} ${Spacings.SPACING_4B}
      ${Spacings.SPACING_6B} ${Spacings.SPACING_4B};
    grid-template: max-content 1fr ${Spacings.SPACING_22B} / 1fr;
    height: auto;
    grid-row-gap: ${Spacings.SPACING_0B};
  }
`;

const Bio = styled.div`
  font-weight: ${Fonts.REGULAR};
  font-size: ${Fonts.H5};
  line-height: ${Fonts.HEADER_LINE_HEIGHT};
  color: ${({ $color }) => $color};
  @media only screen and (max-width: ${mobile.maxWidth}) {
    font-size: ${Fonts.H6};
  }
`;

const Tag = styled.span`
  padding: ${Spacings.SPACING_2} ${Spacings.SPACING_2B};
  color: ${({ $color }) => $color};
  background-color: ${({ $bgColor }) => $bgColor};
  display: inline-block;
  border-radius: ${Spacings.SPACING_1B};
  margin-left: ${({ $showIcon }) =>
    $showIcon ? Spacings.SPACING_14B : Spacings.SPACING_0B};
`;

const ClassInfo = styled.div`
  grid-row: span 2;
  @media only screen and (max-width: ${mobile.maxWidth}) {
    grid-row: span 1;
    padding-top: ${Spacings.SPACING_6B};
  }
  cursor: ${({ $isClickable }) => ($isClickable ? 'pointer' : 'not-allowed')};
`;

const Container = styled.div`
  display: grid;
  grid-template: 1fr / ${({ $showIcon }) => `${$showIcon ? '40px ' : ''}1fr`};
  grid-column-gap: ${Spacings.SPACING_4B};
  padding-top: 10px;
  align-items: center;
`;

const ClassTitle = styled.div`
  font-weight: ${Fonts.REGULAR};
  font-size: ${Fonts.H4};
  line-height: ${Fonts.PARA_LINE_HEIGHT};
  color: #1f2432;
`;

const ClassTime = styled(P1)`
  line-height: ${Fonts.PARA_LINE_HEIGHT};
  color: #7b8ab5;
  padding-top: ${Spacings.SPACING_3B};
  margin-left: ${({ $showIcon }) =>
    $showIcon ? Spacings.SPACING_14B : Spacings.SPACING_0B};
`;

const Share = styled(ShareContainer)`
  margin-top: ${Spacings.SPACING_0B};
  @media only screen and (max-width: ${mobile.maxWidth}) {
    margin-top: ${Spacings.SPACING_10B};
  }
`;

const SpeakerInfoCard = ({ author }) => {
  const isMobileDevice = useContext(IsMobileContext);
  const { classItem, isLoggedIn } = useSelector(({ auth, summit }) => ({
    classItem: summit.speakers.find(
      (speaker) =>
        speaker.username.toLowerCase() === author.username.toLowerCase()
    )?.classItem,
    isLoggedIn: auth.isLoggedIn
  }));
  const {
    name,
    subText,
    username,
    textColor,
    bio,
    svgColor,
    bgColor,
    avatar
  } = author;
  const topicGroupColorMap = useSelector(
    ({ summit }) => summit.topicGroupColorMap || {}
  );
  const {
    permalink,
    relative_link: relativeLink,
    youtubeLink,
    isYoutube,
    programme
  } = classItem || {};
  const colors = classItem
    ? topicGroupColorMap[programme.topic_groups[0].uid] ||
      topicGroupColorMap[programme.topic_groups[1].uid]
    : {};
  const Icon = null;
  const showIcon = Icon != null;
  const isClickable = !!permalink;
  const dispatch = useDispatchToStore();
  const onClassClick = isClickable
    ? () => {
        if (!isLoggedIn) {
          if (isMobileDevice) window.location.href = '/login';
          else dispatch(toggleLoginModal(true));
        } else window.open(isYoutube ? youtubeLink : relativeLink, '_blank');
      }
    : null;
  return (
    <Wrapper>
      <SpeakerContainer $bgColor={bgColor}>
        <Image
          src={
            avatar ||
            `https://quark.uacdn.net/summit/${encodeURIComponent(username)}.png`
          }
        />
        <ContentWrapper>
          <SpeakerName $color={textColor}>{name}</SpeakerName>
          <SubText $color={textColor}>{subText}</SubText>
        </ContentWrapper>
        <CardBackground color={svgColor} />
      </SpeakerContainer>
      <InfoContainer>
        <Bio $color={textColor}>{bio}</Bio>
        {classItem ? (
          <ClassInfo onClick={onClassClick} $isClickable={isClickable}>
            <Tag
              $color={colors?.color}
              $bgColor={colors?.bgColor}
              $showIcon={showIcon}
            >
              {programme.topic_groups[0].name}
            </Tag>
            <Container $showIcon={showIcon}>
              {showIcon && Icon}
              <ClassTitle>{classItem.name}</ClassTitle>
            </Container>
            <ClassTime $showIcon={showIcon}>
              {`${dateHelper.getDateShort(
                classItem.live_at,
                false
              )} at ${dateHelper.getTime(classItem.live_at)}`}
            </ClassTime>
          </ClassInfo>
        ) : (
          <ClassInfo />
        )}
        <Share />
      </InfoContainer>
    </Wrapper>
  );
};

SpeakerInfoCard.propTypes = {
  author: PropTypes.objectOf(PropTypes.any).isRequired
};

export default SpeakerInfoCard;
